(ns app.domain.sign-off.model
  (:require [app.domain.business.model :as business-model]
            [app.domain.party-role.time-travel :as role-time-travel]
            [app.domain.party.model :as party-model]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [core.db.entity.util :as eu]
            [core.user.model :as user-model]
            [potpuri.core :as p]
            [schema-tools.core :as st]
            [schema.core :as s]))


(defn form-all-sign-offs [form]
  (->> form
       ;; Can be called with an old application without the tuple index, so :entity/_parent is necessary
       :entity/_parent
       (filter (eu/is-type-fn :entity.type/sign-off))))


(defn sign-off-entity-for-role
  "Returns (the first) sign-off signature entity for the party-role given as argument or nil. `form` is the
  sign-off form."
  [form {role-id :db/id}]
  (-> (form-all-sign-offs form)
      (p/find-first #(-> % :sign-off/party-role :db/id (= role-id)))))


(defn roles-that-have-not-signed-off
  "Given a sign-off form and a set of roles, returns set difference between given-roles and roles-that-have-signed-off
  i.e. given-roles - roles-that-have-signed-off"
  [form role-keys]
  (let [sign-off-role-keys (->> form
                                (eu/descendants :entity.type/sign-off)
                                (map (comp :party-role/key :sign-off/party-role))
                                set)]
    (set/difference (set role-keys) sign-off-role-keys)))


(defn signed-for-role-keys?
  "Given a sign-off form and a set of roles, returns whether or not all of the given roles have
  signed off the form."
  [form role-keys]
  (empty? (roles-that-have-not-signed-off form role-keys)))


(defn sign-off-time
  "Given a sign-off form and a set of roles, returns the last of the sign off times if all roles
  given as an argument have signed the form. Nil if not."
  [form required-role-keys]
  (when (signed-for-role-keys? form required-role-keys)
    (let [sign-offs (form-all-sign-offs form)]
      (-> sign-offs eu/latest :entity/created))))


(defn disabled-when-application-signed-off?
  "After application signed off, forms with `locked-when-signed-off?` and application-sign-off form
  itself must be disabled"
  [application form]
  (when-let [sign-off-form (:application/sign-off-form application)]
    (and (seq (form-all-sign-offs sign-off-form))
         (or (= sign-off-form form)
             (:form.schema/locked-when-signed-off? (-> form :form/schema))))))


(defn role->field-id [role]
  (keyword "sign-off-signature" (name role)))


(def locked-role-keys
  "These are the roles that can't be edited or added after the application has been signed-off,
  regardless of who the signer was.
  In essence, the application sign-off is assumed to declare that the info for these roles in particular is correct.
  They are also included in the sign-off form, even if they are not signers,
  since the applicant is assumed to sign off on their behalf."
  #{:role/applicant
    :role/property-owner
    :role/builder
    :role/agent})


(def sacred-role-keys
  "Parties that have some of the following roles cannot be removed"
  #{:role/applicant
    :role/property-owner
    :role/agent})


(s/defschema Party
  "Convenience schema for preprocessed party data. Preprocessing is done by
  `app.domain.sign-off.core/parties`."
  (st/merge
    {:db/id     s/Int
     :role-keys #{s/Keyword} ; #{:role/applicant}
     :my-party? s/Bool}
    (st/optional-keys
      {:full-name             s/Str ; Pena Banaani
       :street-address        s/Str ; 350 CITY HALL SQ W
       :postal-code           s/Str ; A1A 1A2
       :municipality-province s/Str ; Windsor, Ontario
       :party/business        (st/merge
                                {:business/names #{{s/Keyword s/Any}}
                                 :business/type  s/Keyword
                                 s/Keyword       s/Any}
                                (st/optional-keys
                                  {:business/id          s/Str
                                   :business/trade-names #{{s/Keyword s/Any}}}))})))

(s/defschema Property
  (st/merge
    {:address     s/Str
     :property/id s/Str
     :primary     s/Bool}
    (st/optional-keys {:property/legal s/Str})))

(s/defschema ProcessedParty
  (st/assoc Party
            ;; The role entities of the party related to sign-off, sorted in descending order of importance
            :sign-off-roles [s/Any]))


(def role-weights
  "The relative priority of each sign-off role.
  Used to sort both roles and parties (according to the sum of their roles)"
  {:role/applicant      8
   :role/agent          6
   :role/property-owner 4
   :role/builder        2})


(defn- sort-by-weight
  "Returns the list in descending order of weight.
  Assigns default weight 0 if the weight-fn returns nil for it."
  [weight-fn items]
  (->> items
       (map #(if-let [weight (weight-fn %)]
               [% weight]
               [% 0]))
       (sort-by second >)
       (map first)))


(defn sort-role-keys
  "Returns the list of the given party roles in descending order of importance"
  [role-keys]
  (sort-by-weight #(get role-weights % 1) role-keys))


(defn sorted-role-keys
  "Returns only the sign-off role keys for the party in descending order of importance"
  [party]
  (some->> (role-time-travel/party-role-keys party)
           (sort-by-weight role-weights)))


(defn- sort-parties-by-weight
  "Sorts the parties in descending order by the sums of their roles' weights.
  Drops any parties that don't have any sign-off roles"
  [role-keys-fn parties]
  (sort-by-weight (fn [party]
                    (some->> (role-keys-fn party)
                             (keep role-weights)
                             seq
                             (apply +)))
                  parties))


(defn filtered-sorted-parties
  "Returns a list of parties only relevant for the sign-off and sorts them in descending order of importance.
  Parties may be time-travelling from before the party role migration."
  [signing-parties sign-off-role-keys parties]
  (let [signer-role-keys (->> signing-parties
                              (map role-time-travel/party-role-keys)
                              (apply set/union))
        other-role-keys  (set/difference sign-off-role-keys signer-role-keys)]
    (->> parties
         (filter (fn [party]
                   (or (some #(-> % :db/id (= (:db/id party))) signing-parties)
                       (some other-role-keys (role-time-travel/party-role-keys party)))))
         (sort-parties-by-weight sorted-role-keys))))


(s/defn process-parties :- [ProcessedParty]
  "Retains only the sign-off parties (see `roles` above).
  Returns ordered parties with `:sign-off-roles` attribute which contains the party's  and sorted sign-off role."
  [party-roles parties :- [Party]]
  (->> parties
       (sort-parties-by-weight :role-keys)
       (map (fn [party]
              (->> (:role-keys party)
                   (sort-by-weight role-weights)
                   (map #(p/find-first party-roles {:party-role/key %}))
                   (assoc party :sign-off-roles))))))


(defn sign-off-status
  "Application sign-off form status (which is also the status for the application).

     #{::signed [& roles]}: Application is signed by all the required roles
     #{::required}: Application needs to be signed by some role
     #{}: Application is not required to (and cannot) be signed."
  [sign-off-form roles-in-config]
  (if sign-off-form
    (let [signed-by (->> (form-all-sign-offs sign-off-form)
                         (map (comp :party-role/key :sign-off/party-role)))]
      (if (and (seq signed-by)
               (empty? (set/difference roles-in-config (set signed-by))))
        (into #{::signed} signed-by)
        #{::required}))
    #{}))


(defn signed-by-authority
  "Return true if all sign offs are made by an authority user."
  [sign-off-form]
  (let [signed-by (form-all-sign-offs sign-off-form)]
    (every? :sign-off/done-by-authority? signed-by)))

(defn any-sign-offs-done? [sign-off-form]
  (->> sign-off-form
       (eu/descendants :entity.type/sign-off)
       seq))

(defn party-roles-locked?
  "Return true if party with `party-roles` should be locked when
  application is signed."
  [sign-off-form party-role-keys locked-role-keys]
  (and sign-off-form
       (any-sign-offs-done? sign-off-form)
       (some (set locked-role-keys) party-role-keys)))

(defn format-signee-name [{:sign-off/keys [signed-for] :as sign-off-entity} tr]
  (if signed-for
    (party-model/simple-name signed-for i18n-model/default-lang)
    (if (:business/names sign-off-entity)
      (tr [:signature/signed-for-corporation]
          [(user-model/full-name sign-off-entity)
           (-> sign-off-entity (business-model/business-name i18n-model/default-lang))])
      (user-model/full-name sign-off-entity))))

(def default-sign-off-role-keys
  [:role/applicant])

(defn required-sign-off-role-keys
  "Returns the required role keys for sign-off configured in the config tree
  sorted by the role priority.

  Note that this returns the current configuration, so if you want an older
  configuration, give args loaded with `d/as-of` namely `sign-off/required-role-keys-at-sign-off-time`"
  [group-configs]
  (let [reversed-configs (reverse group-configs)]
    (or (some->> (or (some->> reversed-configs
                              (mapcat :group.config/sign-off-declarations)
                              seq
                              (map role-time-travel/declaration-config-role-key))
                     (->> reversed-configs
                          (mapcat role-time-travel/required-sign-off-role-keys)))
                 distinct
                 sort-role-keys
                 seq)
        default-sign-off-role-keys)))
